import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { IFunpayExistingAccount, IFunpayLinkExistingData, IFunpayOnboardingPrefill } from '../../../../../services/funpay.service';
import { SHOPPER_STATEMENT_MAX_LENGTH, SHOPPER_STATEMENT_PATTERN } from '../onboarding-form/onboarding-form';
import { SectionCardComponent } from '../../../../../components/shared/section-card/section-card.component';

/** Choose an approved FunPay account in the organisation and give this venue's store details. */
@Component({
    selector: 'funpay-link-existing',
    imports: [ReactiveFormsModule, TranslatePipe, ButtonModule, InputTextModule, MessageModule, SectionCardComponent],
    templateUrl: './link-existing.html'
})
export class FunPayLinkExistingComponent {
    private fb = inject(FormBuilder);

    accounts = input.required<IFunpayExistingAccount[]>();
    prefill = input<IFunpayOnboardingPrefill | null>(null);
    submitting = input(false);
    submitError = input<string | null>(null);

    submitted = output<IFunpayLinkExistingData>();
    back = output<void>();

    selected = signal<string | null>(null);
    maxStatement = SHOPPER_STATEMENT_MAX_LENGTH;

    form = this.fb.nonNullable.group({
        shopperStatement: ['', [Validators.required, Validators.maxLength(SHOPPER_STATEMENT_MAX_LENGTH), Validators.pattern(SHOPPER_STATEMENT_PATTERN)]],
        phoneNumberVenue: ['', [Validators.required, Validators.pattern(/^\+?[0-9 ]{6,20}$/)]],
        street: ['', Validators.required],
        postalCode: ['', Validators.required],
        city: ['', Validators.required]
    });

    private status = toSignal(this.form.statusChanges, { initialValue: this.form.status });
    canSubmit = computed(() => !!this.selected() && this.status() === 'VALID' && !this.submitting());
    selectedAccount = computed(() => this.accounts().find((a) => a.clientId === this.selected()) ?? null);

    constructor() {
        effect(() => {
            const p = this.prefill();
            if (p) {
                this.form.patchValue({
                    shopperStatement: p.shopperStatement.slice(0, SHOPPER_STATEMENT_MAX_LENGTH),
                    phoneNumberVenue: p.phoneNumberVenue,
                    street: p.street,
                    postalCode: p.postalCode,
                    city: p.city
                });
            }
        });
        // One account means there is nothing to choose.
        effect(() => {
            const accounts = this.accounts();
            if (accounts.length === 1 && !this.selected()) {
                this.selected.set(accounts[0].clientId);
            }
        });
    }

    invalid(field: keyof typeof this.form.controls): boolean {
        const control = this.form.controls[field];
        return control.invalid && (control.dirty || control.touched);
    }

    submit(): void {
        this.form.markAllAsTouched();
        const sourceClientId = this.selected();
        if (!sourceClientId || this.form.invalid) {
            return;
        }
        this.submitted.emit({ sourceClientId, ...this.form.getRawValue(), stateOrProvince: this.prefill()?.stateOrProvince ?? '' });
    }
}
